import uuid from 'uuid/v1';
import service from '@/services/linesheet';
import asset, { ICategory } from '@/services/asset';
import { UUID } from '@/types';
import { CurrencyCode } from '@/services/finance';
import productService from '@/services/product';
import priceService, { Price, PriceType } from '@/services/price';
import { DEFAULT_CURRENCY } from '@/constants';
import { store as DirtyCheckComposer } from '@/composers/dirty-check';
import { promiseError } from '@/store/utils';
import {
  Linesheet,
  Product,
  ProductPrices,
  StateLinesheet,
  UserProduct,
  UpdateLinesheet,
} from '../types';

const formatLinesheet = (data: Linesheet): Linesheet => (
  JSON.parse(
    JSON.stringify({
      ...data,
      currencies: data.currencies || [],
      contact: data.contact || {
        address: '',
        companyName: '',
        contactName: '',
        country: '',
        email: '',
        phoneNumber: '',
        website: '',
      },
    }),
  )
);

const getPrices = (
  category: string,
  prices: ProductPrices,
  productId: UUID,
) => {
  const result = [];
  for (const currency in prices) {
    const code = currency as CurrencyCode;
    const type: PriceType = 'product';
    result.push({
      type_id: productId,
      amount: prices[code] || 0,
      category: `${category} (${code})`,
      currency: code,
      type,
    });
  }
  return result;
};

export default ({
  trackMutations,
}: any) => {

  trackMutations({
    CREATE: (l: Linesheet) => ({ id: l.id }),
    REMOVE: (id: UUID) => ({ id }),
    SAVE: (l: Linesheet) => l,
  });

  return DirtyCheckComposer({
    namespaced: true,
    state: () => ({
      data: {},
      saved: {},

      productLibrary: [],
      view: {
        exportStatus: {},
        libraryFail: false,
        readOnly: false,
        selectedProduct: 0,
      },
    } as StateLinesheet),
    getters: {
      productCount: (state: StateLinesheet): number => (
        (state.data.products || []).length
      ),
    },
    mutations: {
      ARCHIVE(state: StateLinesheet, value: boolean) {
        state.data.archived = value;
      },
      CREATE() {
        // created
      },
      LOAD(state: StateLinesheet, data: Linesheet) {
        state.view.readOnly = (this as any).state.auth.user.id !== data.user_id;
        state.data = formatLinesheet(data);
        state.saved = formatLinesheet(data);
      },
      PRODUCT_ADD(state: StateLinesheet, payload?: Product) {
        const { products } = state.data;
        state.data.products = products.concat({
          name: 'Product name',
          price_retail: {},
          price_wholesale: {},
          properties: [],
          assets: [],
          ...payload,

          // create new id and index
          id: uuid(),
          index: products.length,
        });
      },
      PRODUCT_CLEAR(state: StateLinesheet) {
        state.data.products = [];
      },
      PRODUCT_CLONE(state: StateLinesheet, index: number) {
        // reset linked records
        const { products } = state.data;
        // stringify and parse to clear getters/setters from deeply nested objects
        const clone = JSON.parse(JSON.stringify(products[index]));
        const clonedProduct: Product = {
          ...clone,

          // add to end of array and remove all assets
          id: uuid(),
          assets: [],
          name: `${clone.name} (clone)`,
        };

        // clone after current product
        state.data.products = [
          ...products.slice(0, index + 1),
          clonedProduct,
          ...products.slice(index + 1),
        ];
      },
      PRODUCT_EXPORTING(state: StateLinesheet, { index, status }: { index: number, status: string }) {
        state.view.exportStatus[index] = status;
        state.data.products = [...state.data.products];
      },
      PRODUCT_REMOVE(state: StateLinesheet, index: number) {
        state.data.products = [
          ...state.data.products.slice(0, index),
          ...state.data.products.slice(index + 1),
        ];
      },
      PRODUCT_SELECT(state: StateLinesheet, index: number) {
        state.view.selectedProduct = index;
      },
      PRODUCTS_UPDATE(state: StateLinesheet, products: Product[]) {
        state.data.products = products;
      },
      REMOVE(state: StateLinesheet, id: string) {
        // reset all data (prevents isDirty from being true)
        state.data = formatLinesheet({} as Linesheet);
        state.saved = formatLinesheet({} as Linesheet);
      },
      SAVE(state: StateLinesheet) {
        state.saved = {
          ...JSON.parse(
            JSON.stringify(state.data),
          ),
        };
      },
      UPDATE_PRODUCT_LIBRARY(state: StateLinesheet, products: UserProduct[]) {
        state.productLibrary = products;
      },
    },
    actions: {
      archive({ commit }: any, value: boolean) {
        commit('ARCHIVE', value);
      },
      create: ({ commit }: any) => (
        service
          .create({
            currencies: [DEFAULT_CURRENCY],
            date: (new Date()).toISOString(),
            title: 'Linesheet title',
          })
          .then(created => {
            commit('CREATE', created);
            return created;
          })
          .catch(promiseError('LINESHEET_CREATE', commit))
      ),
      load: ({ commit }: any, linesheetId: UUID) => {
        return service.get<Linesheet>(linesheetId, `
          id
          user_id
          archived
          contact
          currencies
          date
          date_created
          notes
          title
          products(
            order_by: {
              index: asc
            }
          ) {
            id
            product_id
            name
            price_retail
            price_wholesale
            properties
            assets {
              id
              linesheet_product_id
              src
            }
            product {
              id
              name
              prices(
                order_by: {
                  index: asc
                }
              ) {
                amount
                category
                currency
              }
              assets (
                limit: 1
                order_by: {
                  index: asc
                }
              ) {
                src
              }
            }
          }
        `).then(result => {
            commit('LOAD', result);
            return result;
          },
          promiseError('LINESHEET_LOAD', commit),
        );
      },
      loadProducts: ({ commit, state }: any) => {
        state.view.libraryFail = false;
        return state.productLibrary.length
          ? Promise.resolve(state.productLibrary)
          : productService
            .find<UserProduct>(`
              id
              name
              properties
              prices(
                order_by: {
                  index: asc
                }
              ) {
                amount
                category
                currency
              }
              assets {
                id
                src
              }
            `, {
              limit: 1000,
            })
            .then(response => {
              commit('UPDATE_PRODUCT_LIBRARY', response.result);
              return response.result;
            }, () => {
              state.view.libraryFail = true;
              promiseError('LINESHEET_UPDATE_PRODUCT_LIBRARY', commit);
            });
      },
      productAdd: ({ commit }: any, payload?: Product) => commit('PRODUCT_ADD', payload),
      productClone({ commit }: any, index: number) {
        commit('PRODUCT_CLONE', index);
        commit('PRODUCT_SELECT', index + 1);
      },
      productExport(
        { commit, state }: { commit: any, state: StateLinesheet },
        index: number,
      ) {
        // update status
        commit('PRODUCT_EXPORTING', { index, status: 'product' });

        // create product
        const payload = state.data.products[index];
        productService
          .create({
            name: payload.name,
            properties: payload.properties,
          })
          .then(response => {
            commit('PRODUCT_EXPORTING', { index, status: 'resource' });

            // update product details with new product details
            const productId = response.id;
            const listOfAssets = payload.assets || [];
            const prices = [
              ...getPrices('Retail', payload.price_retail, productId),
              ...getPrices('Wholesale', payload.price_wholesale, productId),
            ].map((price, priceIndex) => ({
              ...price,
              index: priceIndex,
            }));

            state.data.products[index] = {
              ...payload,
              product_id: productId,
              product: {
                id: productId,
                name: payload.name,
                assets: listOfAssets,
                prices,
              },
            };

            const promises = [
              // copy prices
              priceService
                .create(prices)
                .then(priceResponse => ({
                  // for some reason TS want's this signature
                  // to match with the assets duplicate below
                  affected_rows: 1,
                  returning: [priceResponse],
                })),
            ];

            // copy assets
            listOfAssets.length && promises.push(
              asset.duplicate({
                assetIds: listOfAssets.map(item => item.id),
                typeId: productId,
                type: ICategory.Product,
              }),
            );

            return Promise.all(promises);
          })
          .then(() => commit('PRODUCT_EXPORTING', { index, status: undefined }))
          .catch((e) => {
            console.error(e);
            commit('PRODUCT_EXPORTING', { index, status: 'fail' });
          });
      },
      productRemove({ commit }: any, index: number) {
        commit('PRODUCT_REMOVE', index);
      },
      productsClear({ commit }: any) {
        commit('PRODUCT_CLEAR');
      },
      productSelect({ commit }: any, index: number) {
        commit('PRODUCT_SELECT', index);
      },
      productsUpdate({ commit }: any, products: Product[]) {
        commit('PRODUCTS_UPDATE', products);
      },
      save: (
        { commit, state }:
        { commit: any, state: StateLinesheet<UpdateLinesheet> },
      ) => {
        // save linesheet data
        const linesheetId = state.data.id;
        const stateProducts = state.data.products;
        const updateLinesheet = service.update(linesheetId, {
          ...state.data,
          date_created: new Date().toISOString(),

          // cant save userId
          user_id: undefined,

          // save product without saving linked records
          products: stateProducts.map((product, index) => ({
            ...product,
            // remove linked records and views
            assets: undefined,
            product: undefined,
            index,
          })),
        });

        // determine products to remove
        const { products: currentProducts } = state.data;
        const { products: savedProducts } = state.saved;
        const productDeleteIds = savedProducts
          .filter((savedProduct: Product) => (
            !currentProducts.find((newProduct: Product) => (
              newProduct.id === savedProduct.id
            ))
          ))
          .map(product => product.id);

        // TODO: service method "removeProductsById"
        // should also remove assets
        const removeProducts = productDeleteIds.length
          ? service.removeProductsById(productDeleteIds)
          : Promise.resolve();

        // delete the assets of products being deleted
        const assetDeleteIds = productDeleteIds
          .reduce((assetIds, productId) => {
            const product = savedProducts.find(item => item.id === productId);
            return product && product.assets
              ? assetIds.concat(...product.assets.map(item => item.id))
              : assetIds;
          }, [] as string[]);

        const removeAssets = assetDeleteIds.length
          ? asset.remove({ ids: assetDeleteIds })
          : Promise.resolve();

        return Promise.all([
          removeAssets,
          removeProducts,
          updateLinesheet,
        ]).then(
          () => commit('SAVE', state.data),
          promiseError('LINESHEET_SAVE', commit),
        );
      },
    },
  });
};
